import { useEffect, useLayoutEffect, useRef, useState, type ReactNode, type RefObject } from 'react'
import { createPortal } from 'react-dom'

/**
 * Camada que flutua por cima da página inteira, presa a um elemento âncora.
 *
 * É o que lista suspensa, menu e seletor usam para abrir. Antes cada um abria
 * dentro da própria caixa, com `position: absolute`, e a caixa cortava o que
 * passava da borda dela: o cartão tem `overflow: hidden` para os cantos
 * arredondados, a janela rola por dentro, a ficha do contato também. Foi
 * assim que uma lista de opções abria como uma faixa fina de
 * dois pixels embaixo do campo.
 *
 * Aqui a camada vai para o `body` (fora de qualquer caixa), com posição fixa
 * calculada a partir da âncora:
 * - abre para baixo quando cabe, e para cima quando embaixo falta espaço;
 * - nunca passa das bordas da janela;
 * - acompanha a âncora quando a página ou a caixa de dentro rola, e quando a
 *   janela muda de tamanho;
 * - fecha no Esc sem fechar junto a janela em que está (o Esc é dela antes).
 *
 * Clique dentro dela não conta como "clique fora" para ninguém: o
 * `useFecharAoClicarFora` ignora tudo que estiver dentro de `[data-flutuante]`.
 */

const MARGEM = 8

interface Posicao {
  top: number
  left: number
  width: number
  maxHeight: number
}

export function Flutuante({
  ancora,
  aberto,
  aoFechar,
  children,
  largura = 'ancora',
  larguraMinima = 0,
  alinhar = 'inicio',
  preferir = 'baixo',
  distancia = 6,
  alturaMaxima = 320,
  className,
  role,
  id,
  ariaLabel,
}: {
  ancora: RefObject<HTMLElement | null>
  aberto: boolean
  aoFechar: () => void
  children: ReactNode
  /** `ancora`: a mesma largura do campo. Número: largura fixa, em px. */
  largura?: 'ancora' | number
  larguraMinima?: number
  /** Qual borda da âncora a camada segue. */
  alinhar?: 'inicio' | 'fim'
  preferir?: 'baixo' | 'cima'
  distancia?: number
  alturaMaxima?: number
  className?: string
  role?: string
  id?: string
  ariaLabel?: string
}) {
  const caixa = useRef<HTMLDivElement>(null)
  const [posicao, setPosicao] = useState<Posicao | null>(null)

  const medir = useRef<() => void>(() => undefined)
  medir.current = () => {
    const a = ancora.current?.getBoundingClientRect()
    const el = caixa.current
    if (!a || !el) return

    const largo =
      largura === 'ancora' ? Math.max(a.width, larguraMinima) : Math.max(largura, larguraMinima)
    const altura = Math.min(el.scrollHeight, alturaMaxima)
    const abaixo = window.innerHeight - a.bottom - distancia - MARGEM
    const acima = a.top - distancia - MARGEM
    const paraCima =
      preferir === 'cima' ? acima >= altura || acima > abaixo : altura > abaixo && acima > abaixo
    const maxHeight = Math.max(120, Math.min(alturaMaxima, paraCima ? acima : abaixo))
    const alturaFinal = Math.min(altura, maxHeight)

    const esquerdaDesejada = alinhar === 'fim' ? a.right - largo : a.left
    const left = Math.min(
      Math.max(MARGEM, esquerdaDesejada),
      Math.max(MARGEM, window.innerWidth - largo - MARGEM),
    )
    const top = paraCima ? a.top - distancia - alturaFinal : a.bottom + distancia

    setPosicao((antes) =>
      antes &&
      antes.top === top &&
      antes.left === left &&
      antes.width === largo &&
      antes.maxHeight === maxHeight
        ? antes
        : { top, left, width: largo, maxHeight },
    )
  }

  useLayoutEffect(() => {
    if (!aberto) {
      setPosicao(null)
      return
    }
    medir.current()
  }, [aberto])

  // Acompanha a âncora: rolagem em qualquer caixa, tamanho da janela, e o
  // próprio conteúdo mudando (a busca que filtra a lista muda a altura).
  useEffect(() => {
    if (!aberto) return
    let quadro = 0
    const agendar = () => {
      cancelAnimationFrame(quadro)
      quadro = requestAnimationFrame(() => medir.current())
    }
    window.addEventListener('scroll', agendar, true)
    window.addEventListener('resize', agendar)
    const observador = new ResizeObserver(agendar)
    if (caixa.current) observador.observe(caixa.current)
    return () => {
      cancelAnimationFrame(quadro)
      window.removeEventListener('scroll', agendar, true)
      window.removeEventListener('resize', agendar)
      observador.disconnect()
    }
  }, [aberto])

  /*
    Esc fecha só esta camada. Escutado na janela, na fase de captura, e
    parado ali: chega antes do Esc da janela (Modal) que está por baixo, que
    senão fecharia junto e levaria o que a pessoa estava preenchendo.
  */
  useEffect(() => {
    if (!aberto) return
    const noTeclado = (evento: KeyboardEvent) => {
      if (evento.key !== 'Escape') return
      evento.stopPropagation()
      aoFechar()
    }
    window.addEventListener('keydown', noTeclado, true)
    return () => window.removeEventListener('keydown', noTeclado, true)
  }, [aberto, aoFechar])

  if (!aberto) return null

  return createPortal(
    <div
      ref={caixa}
      data-flutuante=""
      id={id}
      role={role}
      aria-label={ariaLabel}
      style={{
        position: 'fixed',
        top: posicao?.top ?? 0,
        left: posicao?.left ?? 0,
        width: posicao?.width,
        maxHeight: posicao?.maxHeight ?? alturaMaxima,
        // Invisível no primeiro quadro, enquanto a posição é medida.
        visibility: posicao ? 'visible' : 'hidden',
        zIndex: 70,
      }}
      className={className}
    >
      {children}
    </div>,
    document.body,
  )
}
